import type { RoleAcceptPosition } from "../../app/services/roles";

/**
 * BE / DB 側の `accept_position` 値を正規化して FE 用の `RoleAcceptPosition` 値に揃える。
 *
 * 仕様書「メンバーシップポジション API 表記統一」(2026-05-09) では UPPERCASE 3 値
 * （`PRODUCER` / `DIRECTOR` / `SPECIALIST`）に統一されている想定だが、
 * 実 DB / API は小文字（`producer` / `director` / `creator`）で返してくるケースがある。
 * また旧称 `creator` は `SPECIALIST` と等価扱いする。
 * 認識できない値および `null` / `""` / `undefined` は `null` を返す。
 *
 * 表示だけでなく **書き込み経路でも必ず通すこと**。`PATCH /dm/<id>` の
 * `accept_position` は旧 lowercase 値（`producer` / `creator` 等）を送ると
 * 400 Bad Request になる（[dmThreads.ts](../../app/services/dmThreads.ts) の
 * `UpdateDmThreadParams` 参照）。
 */
export const normalizeAcceptPosition = (
  raw: string | null | undefined,
): RoleAcceptPosition | null => {
  if (!raw) return null;
  const upper = raw.trim().toUpperCase();
  switch (upper) {
    case "PRODUCER":
      return "PRODUCER";
    case "DIRECTOR":
      return "DIRECTOR";
    case "SPECIALIST":
    case "CREATOR":
      return "SPECIALIST";
    default:
      return null;
  }
};

/**
 * `accept_position` に応じてロールカード右上に表示するステータスラベル文言。
 *
 * - `PRODUCER` / `DIRECTOR` は責任者プロデューサー / プロデューサーとして
 *   常に再委託可・顧客コミュニケーション権限を内包する位置付けのため、
 *   独自の「役職」ラベルを表示する。この場合は `allow_subcontracting` フラグの
 *   値を無視し、別途「再委託可」バッジは表示しない。
 * - 上記以外（`SPECIALIST`）は `allow_subcontracting=true` の場合に
 *   従来通り「再委託可」バッジを表示する。
 */
const ROLE_STATUS_BY_POSITION: Partial<Record<RoleAcceptPosition, string>> = {
  PRODUCER: "プロデューサー（責任者）",
  DIRECTOR: "プロデューサー",
};

/**
 * `accept_position` に応じて表示する Material Symbols のアイコン名。
 *
 * 「権限の階層」を 1 シリーズで読み取れるよう、視覚的に明確に差がつく組合せにする:
 *  - 最上位（責任者プロデューサー） = `workspace_premium`（月桂冠+星バッジ）
 *  - プロデューサー（中位）         = `license`（証明書/認可）
 *  - 再委託可（機能フラグのみ）     = `hub`（中継ノード）
 *
 * 階層なし＋再委託不可（= 通常スペシャリスト）は null。
 */
const ROLE_ICON_BY_POSITION: Partial<Record<RoleAcceptPosition, string>> = {
  PRODUCER: "workspace_premium",
  DIRECTOR: "license",
};

/**
 * 詳細フォームで「再委託を認める」トグルの代わりに表示する固定ラベル。
 * PRODUCER / DIRECTOR の場合は変更不可で、招聘の意思表明として明示的に
 * 文言を表示する。
 */
const SUBCONTRACTING_LABEL_BY_POSITION: Partial<Record<RoleAcceptPosition, string>> = {
  PRODUCER: "プロデューサー（責任者）として招聘",
  DIRECTOR: "プロデューサーとして招聘",
};

/** カードに表示するステータスラベル文言を返す（無い場合は null）。 */
export const roleStatusLabel = (
  acceptPosition: string | null | undefined,
  allowSubcontracting: boolean,
): string | null => {
  const normalized = normalizeAcceptPosition(acceptPosition);
  if (normalized && ROLE_STATUS_BY_POSITION[normalized]) {
    return ROLE_STATUS_BY_POSITION[normalized] ?? null;
  }
  return allowSubcontracting ? "再委託可" : null;
};

/**
 * `roleStatusLabel()` と同じ判定ロジックで、対応する Material Symbols 名を返す。
 * 表示するアイコンが無い場合は null。
 */
export const roleStatusIcon = (
  acceptPosition: string | null | undefined,
  allowSubcontracting: boolean,
): string | null => {
  const normalized = normalizeAcceptPosition(acceptPosition);
  if (normalized && ROLE_ICON_BY_POSITION[normalized]) {
    return ROLE_ICON_BY_POSITION[normalized] ?? null;
  }
  return allowSubcontracting ? "hub" : null;
};

/**
 * 詳細フォームで「再委託を認める」トグルの代わりに表示する文言。
 * null の場合は通常のトグルを表示する。
 */
export const fixedSubcontractingLabel = (
  acceptPosition: string | null | undefined,
): string | null => {
  const normalized = normalizeAcceptPosition(acceptPosition);
  if (!normalized) return null;
  return SUBCONTRACTING_LABEL_BY_POSITION[normalized] ?? null;
};

/**
 * `accept_position` が PRODUCER / DIRECTOR の場合、allow_subcontracting は
 * 強制的に true として扱う（上位フラグとして再委託権限を内包する仕様）。
 */
export const resolveAllowSubcontracting = (
  acceptPosition: string | null | undefined,
  fallback: boolean,
): boolean => {
  const normalized = normalizeAcceptPosition(acceptPosition);
  if (normalized === "PRODUCER" || normalized === "DIRECTOR") {
    return true;
  }
  return fallback;
};

/**
 * `accept_position` が「再委託・顧客コミュニケーション権限を内包する上位フラグ」
 * かどうか。仕様書 §3-1-7 の挙動に対応。
 */
export const isElevatedAcceptPosition = (
  acceptPosition: string | null | undefined,
): boolean => {
  const normalized = normalizeAcceptPosition(acceptPosition);
  return normalized === "PRODUCER" || normalized === "DIRECTOR";
};
